import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GenericFormService } from '../service/generic-form.service';
import { FieldModel } from '../model/field.model';
import { debounceTime, distinctUntilChanged, EMPTY, merge, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { ObjectUtils } from '@cv-invision/core';

@Component({
	selector: 'cv-dropdown-field',
	templateUrl: './dropdown-field.component.html',
	styleUrls: ['./dropdown-field.component.scss']
})
export class DropdownFieldComponent implements OnInit, OnDestroy {
	@Input() fieldOptions: any;
	@Input() control: FormControl;
	/**
	 * IMPORTANT!!!!!
	 * The component need to access and modify field.options.
	 * Reason: On the FormBuilderConfig, the UiEvent.OnChange
	 * can trigger the update of other field.options, hence,
	 * IT IS REQUIRED TO KEEP USING field.options
	 * instead of creating and using a "options" class variable.
	 */
	@Input() field: FieldModel;
	@Input() value: any;
	serverFilterText = '';
	private atComponentDestroy: Subject<any> = new Subject<any>();
	private filterQuery$ = new Subject<string>();
	private immediateQuery$ = new Subject<string>();
	private readonly serverFilterDebounceMs = 700;

	constructor(private formService: GenericFormService) { }

	get useServerFilter(): boolean {
		return !!this.field?.UIOptions?.LoadFromServer;
	}

	/**
	 * True when Autocomplete/Dropdown/MultiSelect should hit a server search pipeline.
	 * @returns boolean
	 */
	private get hasServerOptionsSource(): boolean {
		const ui = this.field?.UIOptions;
		return !!(ui?.LoadFromServer || ui?.SearchFunction || ui?.Url);
	}

	ngOnInit(): void {
		if (this.field.disabled) {
			this.control.disable();
		}

		// Merge the immediate query and the filtered query
		// and subscribe to the result
		merge(
			this.immediateQuery$,
			this.filterQuery$.pipe(debounceTime(this.serverFilterDebounceMs), distinctUntilChanged())
		)
			.pipe(
				switchMap((query) => this.getServerOptions$(query)),
				takeUntil(this.atComponentDestroy)
			)
			.subscribe((result) => this.parseResult(result));
	}

	/**
	 * Trigger the field onChange
	 *
	 * @param $event
	 * @param field
	 * @param row
	 */
	onChange($event: any): void {
		if ($event.value) {
			const selectedIndex = this.field.options.findIndex((item) => item.value === $event.value);

			if ('UIOptions' in this.field) {
				this.field.UIOptions['SelectedValue'] = ObjectUtils.recursiveDeepCopy(
					this.field.options[selectedIndex]
				);
			}
		}

		if ('UIEvents' in this.field && this.field.UIEvents.onChange) {
			this.formService.onChangeOptions$.next({
				field: this.field,
				fieldOptions: this.fieldOptions,
				event: $event
			});
		}
	}

	/**
	 * Trigger the field onSelect
	 *
	 * @param $event
	 * @param field
	 * @param row
	 */
	onSelect($event: any): void {
		if (this.field.UIEvents.onSelect) {
			this.formService.onSelectOptions$.next({
				field: this.field,
				event: $event
			});
		}
	}

	/**
	 * Trigger the field onClear
	 *
	 * @param $event
	 */
	onClear($event: any): void {
		this.control.setValue(null);
		if (this.field.UIEvents.onClear) {
			this.formService.onClear$.next({
				field: this.field,
				event: $event
			});
		}
	}

	/**
	 * Sync cv-multiselect-search values with the form control.
	 *
	 * @param $event selected values from MultiselectSearchComponent
	 */
	onMultiselectSearchChange($event: any): void {
		this.control.setValue($event ?? []);
		this.control.markAsDirty();
		this.onChange({ value: $event });
	}

	/**
	 * Debounced server search while the user types in Dropdown/MultiSelect/Autocomplete.
	 * No-op for static options (keeps field.options intact).
	 */
	onServerFilter($event: any): void {
		if (!this.hasServerOptionsSource) {
			return;
		}
		const query = $event?.query ?? $event?.filter ?? '';
		this.filterQuery$.next(query);
	}

	/**
	 * Server-side MultiSelect search input (bypasses PrimeNG client filtering).
	 */
	onServerFilterTextChange(query: string): void {
		this.serverFilterText = query ?? '';
		if (!this.hasServerOptionsSource) {
			return;
		}
		this.filterQuery$.next(this.serverFilterText);
	}

	/**
	 * Immediate load/filter (panel open). Prefer onServerFilter for typing.
	 *
	 * @param $event
	 */
	filterOptions($event: any): void {
		if (!this.hasServerOptionsSource) {
			return;
		}
		const query = $event?.query ?? $event?.filter ?? this.serverFilterText ?? '';
		this.immediateQuery$.next(query);
	}

	/**
	 * Trigger the field onMultiSelectPanelShow
	 */
	onMultiSelectPanelShow(): void {
		if (!this.useServerFilter) {
			return;
		}
		this.immediateQuery$.next(this.serverFilterText || '');
	}

	/**
	 * Trigger the field onMultiSelectPanelHide
	 */
	onMultiSelectPanelHide(): void {
		this.serverFilterText = '';
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.complete();
		this.filterQuery$.complete();
		this.immediateQuery$.complete();
	}

	/**
	 * Get the server options
	 * @param query
	 * @returns Observable<any>
	 */
	private getServerOptions$(query: string): Observable<any> {
		// If the field has a SearchFunction, use it
		if (this.field.UIOptions?.SearchFunction) {
			return this.field.UIOptions.SearchFunction(query) as Observable<any>;
		}

		// If the field has a Url, use it
		if (this.field.UIOptions?.Url) {
			return this.formService.getDataByUrl(this.field.UIOptions.Url, this.field.UIOptions.FilterName, query);
		}

		// No server source: skip parseResult so static Autocomplete/Dropdown options are preserved.
		return EMPTY;
	}

	/**
	 * Parse result and map them to the required format
	 * @param httpResponse
	 */
	private parseResult(httpResponse: any): void {
		if (httpResponse) {
			this.parseOptions(httpResponse);
		} else {
			this.setOptions([]);
		}
	}

	/**
	 * Parse options from response and map them to the required format
	 * @param response
	 */
	private parseOptions(response: any): void {
		let options = [];

		if (response) {
			if (Array.isArray(response)) {
				options = response.map((o: any) => {
					let idFieldName = 'id';
					let nameFieldName = 'name';
					let optionValueName = 'id';
					let optionLabelName = 'name';

					if ('UIOptions' in this.field && 'OptionsMapper' in this.field.UIOptions) {
						const optionsMapper = this.field.UIOptions.OptionsMapper;
						if (optionsMapper.id) {
							idFieldName = optionsMapper.id;
						}
						if (optionsMapper.name) {
							nameFieldName = optionsMapper.name;
						}

						if (optionsMapper.optionValue) {
							optionValueName = optionsMapper.optionValue;
						}
						if (optionsMapper.optionLabel) {
							optionLabelName = optionsMapper.optionLabel;
						}
					}

					return {
						[optionValueName]: o[idFieldName],
						[optionLabelName]: o[nameFieldName]
					};
				});
			} else {
				for (const key in response) {
					options.push({
						value: key,
						label: response[key]
					});
				}
			}
		}

		if (this.field.UIOptions['SelectedValue']) {
			const optionSelected = this.field.UIOptions['SelectedValue'];
			const optionSelectedIndex = (options || []).findIndex((item) => item.id === optionSelected.value);

			if (optionSelectedIndex < 0) {
				options.push(optionSelected);
			}
		}
		this.mergeSelectedMultiSelectOptions(options);
		this.setOptions(options);
	}

	/**
	 * Set the options
	 * @param options
	 */
	private setOptions(options: any[]): void {
		this.field.options = options;
	}

	/**
	 * Keep currently selected MultiSelect values in options after a server filter refresh.
	 * @param options
	 * @returns void
	 */
	private mergeSelectedMultiSelectOptions(options: any[]): void {
		if (this.field.UIControl !== 'MultiSelect') {
			return;
		}

		const selected = this.control?.value;
		if (!Array.isArray(selected) || !selected.length) {
			return;
		}

		const valueKey = this.field.UIOptions?.OptionsMapper?.optionValue || this.field.UIOptions?.OptionValue || 'id';
		selected.forEach((sel: any) => {
			const selValue = typeof sel === 'object' ? sel?.[valueKey] : sel;
			const exists = options.some((opt: any) => (typeof opt === 'object' ? opt?.[valueKey] : opt) === selValue);
			if (!exists && typeof sel === 'object') {
				options.push(sel);
			} else if (!exists) {
				const option = this.field.options.find((opt: any) => (typeof opt === 'object' ? opt?.[valueKey] : opt) === selValue);
				if (option) {
					options.push(option);
				};
			}

		});
	}
}
